/**
 * FirmaOperatoreModal — firma operatore per l'attribuzione desktop ("chi ha fatto cosa").
 * Riusa gli endpoint già esistenti (mobile_operators):
 *   - GET  /api/mobile-operators/admin/operators?active=true  → chi può FIRMARE (esclude solo_delega)
 *   - GET  /api/mobile-operators/esecutori                    → chi può essere "Eseguito da" (attivi + delega)
 *   - POST /api/mobile-operators/auth/verify-pin              → verifica PIN (no sessione)
 * Ritorna { firmatarioId, firmatario, eseguitoDa } al conferma.
 * NON genera sessione: è solo attribuzione (l'autorizzazione resta il login admin).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { X, ArrowLeft, Loader2, UserCheck, Users } from 'lucide-react';

export interface FirmaConfermata {
  firmatarioId: string | number;
  firmatario: string;      // display name del responsabile (autenticato)
  eseguitoDa: string;      // esecutore reale (default = firmatario)
}

interface Op { id: string | number; displayName: string; role?: string; soloDelega?: boolean }

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (f: FirmaConfermata) => void;
  subtitle?: string;
}

const auth = () => { try { const t = localStorage.getItem('accessToken'); return t ? { Authorization: `Bearer ${t}` } : {}; } catch { return {}; } };

export default function FirmaOperatoreModal({ isOpen, onClose, onConfirm, subtitle }: Props) {
  const [operatori, setOperatori] = useState<Op[]>([]);
  const [esecutori, setEsecutori] = useState<Op[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Op | null>(null);
  const [pin, setPin] = useState('');
  const [eseguitoDaId, setEseguitoDaId] = useState<string>('self');
  const [verifying, setVerifying] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const pinRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null); setPin(''); setEseguitoDaId('self'); setErrore(null);
    setLoading(true);
    Promise.all([
      fetch('/api/mobile-operators/admin/operators?active=true', { headers: { ...auth() } }).then(r => r.json()).catch(() => null),
      fetch('/api/mobile-operators/esecutori', { headers: { ...auth() } }).then(r => r.json()).catch(() => null),
    ]).then(([opsRes, esecRes]) => {
      const ops: Op[] = (opsRes?.operators || []).filter((o: Op) => !o.soloDelega);
      setOperatori(ops);
      setEsecutori(esecRes?.esecutori || []);
    }).finally(() => setLoading(false));
  }, [isOpen]);

  useEffect(() => { if (selected) setTimeout(() => pinRef.current?.focus(), 50); }, [selected]);

  const eseguitoDaNome = useMemo(() => {
    if (eseguitoDaId === 'self') return selected?.displayName || '';
    return esecutori.find(e => String(e.id) === eseguitoDaId)?.displayName || '';
  }, [eseguitoDaId, esecutori, selected]);

  const conferma = async () => {
    if (!selected || pin.length < 4) return;
    setVerifying(true); setErrore(null);
    try {
      const res = await fetch('/api/mobile-operators/auth/verify-pin', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ operatorId: selected.id, pin }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'PIN non valido');
      onConfirm({ firmatarioId: selected.id, firmatario: selected.displayName, eseguitoDa: eseguitoDaNome || selected.displayName });
    } catch (e: any) {
      setErrore(e.message || 'PIN non valido'); setPin(''); pinRef.current?.focus();
    } finally { setVerifying(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-400" />
            <div>
              <h3 className="text-white font-semibold leading-tight">Firma operatore</h3>
              <p className="text-xs text-slate-400">{subtitle || 'Chi sei? Conferma col PIN'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
          ) : !selected ? (
            <>
              <p className="text-sm text-slate-300 mb-3">Seleziona operatore:</p>
              {operatori.length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center">Nessun operatore attivo con credenziali.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {operatori.map(o => (
                    <button key={o.id} onClick={() => setSelected(o)}
                      className="px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 hover:border-emerald-500/60 hover:bg-emerald-500/10 text-left transition-colors">
                      <div className="text-sm text-white font-medium truncate">{o.displayName}</div>
                      {o.role && <div className="text-[10px] text-slate-400 uppercase">{o.role}</div>}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => { setSelected(null); setPin(''); setErrore(null); }}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-white mb-3">
                <ArrowLeft className="w-3.5 h-3.5" /> Cambia operatore
              </button>
              <div className="mb-3 text-sm text-white">Firma: <span className="font-semibold text-emerald-300">{selected.displayName}</span></div>

              {/* Eseguito da (firma "per conto di") */}
              <label className="flex items-center gap-1.5 text-xs text-slate-400 mb-1"><Users className="w-3.5 h-3.5" /> Eseguito da</label>
              <select value={eseguitoDaId} onChange={e => setEseguitoDaId(e.target.value)}
                className="w-full mb-4 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white">
                <option value="self">Se stesso ({selected.displayName})</option>
                {esecutori.filter(e => String(e.id) !== String(selected.id)).map(e => (
                  <option key={e.id} value={String(e.id)}>{e.displayName}{e.soloDelega ? ' — senza credenziali' : ''}</option>
                ))}
              </select>

              <label className="text-xs text-slate-400 block mb-1">PIN operatore</label>
              <input ref={pinRef} type="password" inputMode="numeric" maxLength={8} value={pin}
                onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setErrore(null); }}
                onKeyDown={e => { if (e.key === 'Enter') conferma(); }}
                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-center text-lg tracking-widest mb-2"
                placeholder="••••" />
              {errore && <div className="text-xs text-red-400 mb-2">{errore}</div>}
              <button onClick={conferma} disabled={pin.length < 4 || verifying}
                className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-400 rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
                {verifying ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifica…</> : 'Conferma firma'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
